import CheckBoxComponent from 'app_components/commons/checkBox/containers/checkBoxComponent'
import LabelComponent from 'app_components/commons/LabelComponent/LabelComponent'
import parse from 'html-react-parser'
import LoadingWrapper from 'app_components/commons/LoadingWrapper/LoadingWrapper'
import CertificateMultipleService from 'app_services/admin/certificateMultiple/CertificateMultipleService'
import { ICertificateMultipleGenerateParams, ICertificateStudentProcessed, ICertificatesStudent } from 'app_types/admin/certificateSettings/certificateSettings.types'
import { ICourseSchedulingListItem } from 'app_types/admin/courseScheduling/courseSchedulingTypes'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import CertificateStudentItem from '../CertificateStudentItem/CertificateStudentItem'
import './ModalGenerateMultipleCertificates.scss'
import { MdHorizontalRule } from 'react-icons/md'
import TextComponent from 'app_components/commons/TextComponent/TextComponent'
import DurationFieldService from 'app_services/components/durationFieldService'
import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import TableItemsPaginator from 'app_components/modules/TableItemsPaginator/TableItemsPaginator'
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'

const ITEMS_PER_PAGE = 10

interface IModalGenerateMultipleCertificatesProps {
  onClose: (saved?: boolean) => void
  open: boolean
  courseScheduling: ICourseSchedulingListItem
}

enum GeneralCheckboxState {
  PARTIAL = 'partial',
  ALL = 'all',
  NONE = 'none'
}

const ModalGenerateMultipleCertificates: FC<IModalGenerateMultipleCertificatesProps> = ({
  onClose,
  open,
  courseScheduling,
}) => {
  const certificateNamehasParentheses = (certificateName: string) => /^(\(.*\))/.test(certificateName.trim())

  const certificateIsEnabled = (certificateProcessed: ICertificateStudentProcessed) => {
    if (courseScheduling?.isMultiple && courseScheduling?.certificationStrategy === 'huella2') {
      const hasParentheses = certificateNamehasParentheses(certificateProcessed?.certificateName)
      if (hasParentheses && certificateProcessed.approved) return true
    } else {
      if (certificateProcessed.approved) return true
    }
    return false
  }

  const [warnings, setWarnings] = useState<{key: string, message: string}[]>([])
  const [certificateStudents, setCertificateStudents] = useState<ICertificatesStudent[]>([])
  const certificateMultipleService = useMemo(() => new CertificateMultipleService(), [])
  const durationFieldService = useMemo(() => new DurationFieldService(), [])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isGenerateLoading, setIsGenerateLoading] = useState<boolean>(false)
  const [selectedCertificates, setSelectedCertificates] = useState<Record<string, string[]>>({})
  const screenNotifications = useMemo(() => new ScreenNotificationsUtil(), [])
  const generalCheckboxState: GeneralCheckboxState = useMemo((): GeneralCheckboxState => {
    if (!Object.keys(selectedCertificates)?.length) return GeneralCheckboxState.NONE
    let isCompleted = true
    for(const certificate of certificateStudents) {
      const activeCertificates = certificate?.certifications?.filter((item) => certificateIsEnabled(item))
      if (!activeCertificates?.length) continue;
      const selected = selectedCertificates[certificate.userId]
      if (!selected?.length || selected?.length !== activeCertificates?.length) {
        isCompleted = false
        break;
      }
    }
    return isCompleted ? GeneralCheckboxState.ALL : GeneralCheckboxState.PARTIAL
  }, [selectedCertificates, certificateStudents])

  const handleCloseModal = () => {
    onClose()
  }

  const getCertificateStudents = useCallback(async () => {
    if (!courseScheduling?._id) return
    setIsLoading(true)
    try {
      const response = await certificateMultipleService.getData({
        course_scheduling: courseScheduling?._id,
        without_certification: true,
      })
      if (response?.code === 200) {
        setCertificateStudents(response.students)
        if (response.warnings) {
          setWarnings(response.warnings)
        }
      } else {
        setCertificateStudents([])
      }
    } catch (e) {
      console.error('Certifications Data ERROR: ', e)
    }
    setIsLoading(false)
  }, [certificateMultipleService, courseScheduling?._id])

  useEffect(() => {
    getCertificateStudents()
  }, [getCertificateStudents])

  const handleChangeSelections = (selected: string[], userId: string) => {
    if (selected?.length) {
      setSelectedCertificates({ ...selectedCertificates, [userId]: selected })
    } else {
      const newData = { ...selectedCertificates }
      delete newData[userId]
      setSelectedCertificates({...newData})
    }
  }

  const getSelectedCertificatesByUser = (userId: string) => {
    const selected = selectedCertificates[userId]
    return selected?.length ? selected : []
  }

  const handleClickGeneralCheckbox = () => {
    if (generalCheckboxState !== GeneralCheckboxState.ALL) {
      const newSelected = certificateStudents?.reduce((accum: Record<string, string[]>, certificate) => {
        const activeCertificates = certificate?.certifications?.filter((item) => certificateIsEnabled(item))
        if (activeCertificates?.length) {
          accum[certificate.userId] = activeCertificates?.map((item) => item.certificateSettingId)
        }
        return accum
      }, {})
      setSelectedCertificates(newSelected)
    } else {
      setSelectedCertificates({})
    }
  }

  const handleClickGenerate = async () => {
    if (!courseScheduling?._id) return
    const { hasWarnings, warnings: warningsLi } = showWarnings(false)
    if (hasWarnings && warningsLi) {
      let confirm = screenNotifications.fire({
        message: warningsLi as string,
      },
      {
        type: 'error',
        confirmButtonText: `Continuar`,
        cancelButtonText: `Cancelar`,
        showCancelButton: true,
      })

      if (confirm) {

        confirm.then(async (result) => {
          if (result.value) {
            submitForm()
          }
        })
      }
    } else {
      submitForm()
    }

  }

  const submitForm = async () => {
    if (!courseScheduling?._id) return
    setIsGenerateLoading(true)
    try {
      const params: ICertificateMultipleGenerateParams = {
        courseSchedulingId: courseScheduling?._id,
        students: Object.keys(selectedCertificates)?.map((key) => {
          const certificatesByUser = certificateStudents.find((student: any) => student.userId.toString()  === key.toString())
          return {
            userId: String(key),
            certificateSettings: selectedCertificates[key].reduce((accum: any, element: string) => {
              const certificate = certificatesByUser?.certifications.find((c) => c.certificateSettingId.toString() === element.toString())
              if (certificate) {
                accum.push({certificateSettingId: element, isPartial: certificate?.isPartial || false})
              }
              return accum
            }, []),
          }
        })
      }
      const response = await certificateMultipleService.generateCertificates(params)
      if (response?.code === 200) {
        screenNotifications.showSuccessTimer()
        handleCloseModal()
      }
    } catch (e) {
      console.error('Certifications Generate ERROR: ', e)
    }
    setIsGenerateLoading(false)
  }

  const showWarnings = (component = true) => {
    let warningsArr;
    if (warnings && Array.isArray(warnings) && warnings.length > 0) {
      const warningsLi: any = warnings?.reduce((accum: string, element: {key: string, message: string}) => {
        accum += `<li>${element.key}: ${element.message}</li>`
        return accum;
      }, '')
      if (component) {
        warningsArr = (
          <div className="alert alert-warning">
            <p><b>Información relevante para la generación de certificados</b></p>
            <ul>
              {parse(warningsLi)}
            </ul>
          </div>
        )
      } else {
        warningsArr = `
        <div className="alert alert-warning">
          <p><b>Información relevante para la generación de certificados</b></p>
          <ul>
            ${warningsLi}
          </ul>
        </div>`
      }
    }
    return {hasWarnings: !!warningsArr, warnings: warningsArr};
  }

  return (
    <Modal
      show={open}
      onHide={handleCloseModal}
      size='lg'
    >
      <Modal.Header>
        <Modal.Title>
          Generar certificados
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-generate-multiple-certificates--layout">
          <div className="modal-generate-multiple-certificates--header">
            {showWarnings().warnings}
            <TextComponent color="black">
              <strong>Modalidad:</strong> {courseScheduling?.schedulingMode?.name}
            </TextComponent>
            <TextComponent color="black">
              <strong>Intensidad:</strong> {durationFieldService.getDurationFormated(courseScheduling?.duration)}
            </TextComponent>
            <TextComponent color="black">
              <strong>Ciudad:</strong> {courseScheduling?.city?.name}
            </TextComponent>
            <TextComponent color="black">
              <strong>País:</strong> {courseScheduling?.country?.name}
            </TextComponent>
          </div>
          <div className='modal-generate-multiple-certificates--select-all'>
            <CheckBoxComponent
              checked={generalCheckboxState !== GeneralCheckboxState.NONE}
              icon={generalCheckboxState === GeneralCheckboxState.PARTIAL ? <MdHorizontalRule /> : undefined}
              onClick={handleClickGeneralCheckbox}
            />
            <LabelComponent>
              Seleccionar todo
            </LabelComponent>
          </div>
          <LoadingWrapper loading={isLoading}>
            <TableItemsPaginator items={certificateStudents} perPage={ITEMS_PER_PAGE} >
              {(itemsToRender) => (
                <div className="modal-generate-multiple-certificates--list-items">
                  {itemsToRender?.map((certificate: ICertificatesStudent) => (
                    <CertificateStudentItem
                      key={certificate.userId}
                      certificate={certificate}
                      onChangeSelections={(selected) => handleChangeSelections(selected, certificate.userId)}
                      selectedCertifications={getSelectedCertificatesByUser(certificate.userId)}
                      courseScheduling={courseScheduling}
                    />
                  ))}
                </div>
              )}
            </TableItemsPaginator>
          </LoadingWrapper>
          <div className="modal-generate-multiple-certificates--actions">
            <ButtonComponent onClick={handleCloseModal}>
              Cancelar
            </ButtonComponent>
            <ButtonComponent
              onClick={handleClickGenerate}
              variant='primary'
              disabled={isGenerateLoading || !Object.keys(selectedCertificates)?.length}
              loading={isGenerateLoading}
            >
              Guardar
            </ButtonComponent>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default ModalGenerateMultipleCertificates
